import React, { ChangeEvent, useEffect, useState } from 'react';
import { Header } from 'src/components/Headers';
import { MaintenancePackage } from 'src/constants/modules/maintenancePackage';
import { MAINTENANCE_PACKAGE_AUTH } from 'src/constants/permissions';
import {
    Box,
    Container,
    Divider,
    Grid,
    makeStyles,
    Theme,
} from '@material-ui/core';
import Page from 'src/components/Page';
import {
    canDeleteMaintenancePackage,
    canUpdateMaintenancePackage,
} from 'src/modules/maintenancePackages/helpers/permissionsInMaintenancePackages';
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useMaintenancePackage } from 'src/hooks';
import { useParams } from 'react-router';
import { CapitalizeNames } from 'src/utils/capitalize';
import { InformationCard } from 'src/components/Cards';
import { TDetailsType } from 'src/components/Cards/types';
import moment from 'moment';
import { MAINTENANCE_PACKAGE_DETAILS_POPULATE } from './constants';
import { MaintenancePackageDetailsCard } from './components';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        minHeight: '100%',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
    },
}));

/**
 * Will return the maintenance package details page view
 *
 * @return {*}
 */
export const MaintenancePackageDetails = () => {
    const classes = useStyles();
    const { user } = useAuth();
    const { t } = useTranslation();
    const [details, setDetails] = useState<TDetailsType[] | null>(null);
    const {
        getMaintenancePackageById,
        updateMaintenancePackageById,
        deleteMaintenancePackageById,
        maintenancePackage,
    } = useMaintenancePackage();
    const { maintenancePackageId } = useParams<{
        maintenancePackageId: string;
    }>();

    const canDelete = canDeleteMaintenancePackage(user);

    const canUpdate = canUpdateMaintenancePackage(user);

    const handleToggleActive = (event: ChangeEvent<HTMLInputElement>) => {
        updateMaintenancePackageById(
            maintenancePackageId,
            {
                isActive: event.target.checked,
            },
            {
                populate: MAINTENANCE_PACKAGE_DETAILS_POPULATE,
            }
        );
    };

    useEffect(() => {
        if (maintenancePackageId)
            getMaintenancePackageById(maintenancePackageId, {
                isReadPermissionNecessary: true,
                populate: MAINTENANCE_PACKAGE_DETAILS_POPULATE,
            });
    }, [maintenancePackageId]);

    useEffect(() => {
        if (maintenancePackage?._id) {
            setDetails([
                {
                    label: t('maintenancePackage.Name'),
                    value: maintenancePackage.name,
                },
                {
                    label: t('maintenancePackage.CreatedAt'),
                    value: moment(maintenancePackage.createdAt).format(
                        'DD MMMM YYYY [-] HH:mm a'
                    ),
                },
                {
                    label: t('maintenancePackage.CreatedBy'),
                    value:
                        typeof maintenancePackage.createdBy === 'string'
                            ? maintenancePackage.createdBy
                            : maintenancePackage.createdBy?.name || '',
                },
                {
                    label: t('maintenancePackage.UpdatedAt'),
                    value: maintenancePackage.updatedAt
                        ? moment(maintenancePackage.updatedAt).format(
                              'DD MMMM YYYY [-] HH:mm a'
                          )
                        : t('maintenancePackage.NoUpdated'),
                },
                {
                    label: t('maintenancePackage.UpdatedBy'),
                    value:
                        typeof maintenancePackage.updatedBy === 'string'
                            ? maintenancePackage.updatedBy
                            : maintenancePackage.updatedBy?.name ||
                              t('maintenancePackage.NoUpdated'),
                },
            ]);
        }
    }, [maintenancePackage]);

    const handleDelete = async () =>
        await deleteMaintenancePackageById(maintenancePackageId);

    return (
        <Page
            className={classes.root}
            title={t('maintenancePackage.DetailsView')}
        >
            <Container maxWidth={false}>
                <Header
                    module={MaintenancePackage.name}
                    route={MaintenancePackage.route}
                    crud={{
                        delete: canDelete,
                        goBack: true,
                        update: canUpdate,
                    }}
                    onDelete={handleDelete}
                    id={maintenancePackage?._id}
                    permission={MAINTENANCE_PACKAGE_AUTH}
                    title={CapitalizeNames(maintenancePackage?.name)}
                />
                <Divider />
                <Box mt={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={4}>
                            <InformationCard
                                title={t('maintenancePackage.Info')}
                                details={details}
                                toggleButton={
                                    canUpdate
                                        ? {
                                              checked:
                                                  maintenancePackage?.isActive ||
                                                  false,
                                              onChange: handleToggleActive,
                                          }
                                        : undefined
                                }
                            />
                        </Grid>

                        <Grid item xs={6} sm={8}>
                            <MaintenancePackageDetailsCard
                                details={maintenancePackage?.details || []}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </Page>
    );
};
